import is from '@sindresorhus/is';
import type { QueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { FamilyWithClusters } from '~/shared/data-contract/job-family';

import type { ApplicationFetchers } from '../../fetchers/create-application-fetchers';
import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { jobArchitectureKeys } from './query-keys';

const STALE_TIME_MS = 10_000;

const createQueryFn =
    (fetchFamiliesWithClusters: ApplicationFetchers['fetchFamiliesWithClusters']) =>
    (jobArchitectureId: string) =>
    async ({ signal }: { signal: AbortSignal }): Promise<readonly FamilyWithClusters[]> => {
        const result = await fetchFamiliesWithClusters(jobArchitectureId, { signal });

        if (result.isOk !== true) {
            throw result.error;
        }

        return result.value;
    };

type SelectFn<T> = (data: readonly FamilyWithClusters[]) => T;

export const useFamiliesWithClustersQuery = <TData = readonly FamilyWithClusters[]>(
    jobArchitectureId: string,
    options?: { readonly enabled?: boolean; readonly select?: SelectFn<TData> },
): UseQueryResult<TData, Error> => {
    const { fetchFamiliesWithClusters } = useApplicationFetchers();

    const queryOptions: UseQueryOptions<readonly FamilyWithClusters[], Error, TData> = {
        enabled: options?.enabled ?? true,
        queryFn: createQueryFn(fetchFamiliesWithClusters)(jobArchitectureId),
        queryKey: jobArchitectureKeys.familiesWithClusters(jobArchitectureId),
        staleTime: STALE_TIME_MS,
        throwOnError: false,
    };

    if (options?.select !== undefined) {
        queryOptions.select = options.select;
    }

    return useQuery(queryOptions);
};

export const useFamiliesWithClusters = (
    jobArchitectureId: string,
    options?: { readonly enabled?: boolean },
): UseQueryResult<readonly FamilyWithClusters[], Error> => useFamiliesWithClustersQuery(jobArchitectureId, options);

export const useIsFamilyGenerating = (jobArchitectureId: string, jobFamilyId: string): boolean => {
    const { data: family } = useFamiliesWithClustersQuery(jobArchitectureId, {
        select: (families) => families.find((f) => f.id === jobFamilyId),
    });

    if (is.undefined(family)) {
        return false;
    }

    return (
        family.generationInfo?.inProgress === true ||
        family.clusters.some((cluster) => cluster.generationInfo?.inProgress === true)
    );
};

export const getFamiliesWithClustersCache = (
    queryClient: QueryClient,
    jobArchitectureId: string,
): readonly FamilyWithClusters[] | undefined =>
    queryClient.getQueryData<readonly FamilyWithClusters[]>(
        jobArchitectureKeys.familiesWithClusters(jobArchitectureId),
    );

export const setFamiliesWithClustersCache = (
    queryClient: QueryClient,
    jobArchitectureId: string,
    value: readonly FamilyWithClusters[] | undefined,
): void => {
    queryClient.setQueryData(jobArchitectureKeys.familiesWithClusters(jobArchitectureId), value);
};

export const updateFamiliesWithClustersCache = (
    queryClient: QueryClient,
    jobArchitectureId: string,
    updater: (old: readonly FamilyWithClusters[]) => readonly FamilyWithClusters[],
): void => {
    queryClient.setQueryData<readonly FamilyWithClusters[] | undefined>(
        jobArchitectureKeys.familiesWithClusters(jobArchitectureId),
        (old) => (is.undefined(old) ? undefined : updater(old)),
    );
};

export const updateClustersForFamily = (
    queryClient: QueryClient,
    jobArchitectureId: string,
    jobFamilyId: string,
    updater: (old: FamilyWithClusters['clusters']) => FamilyWithClusters['clusters'],
): void => {
    updateFamiliesWithClustersCache(queryClient, jobArchitectureId, (families) =>
        families.map((family) =>
            family.id === jobFamilyId ? { ...family, clusters: [...updater(family.clusters)] } : family,
        ),
    );
};
